import { useState, useEffect } from 'react';
import { theme, buttonStyles, modalStyles, spacing, typography, borderRadius, mergeStyles, isMobile, mobileSpacing } from '../styles/theme';
import { trackUserEvent } from '../analytics';

export default function HowItWorksModal({ onClose }) {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isClosing, setIsClosing] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  useEffect(() => {
    trackUserEvent('how_it_works_opened');
  }, []);

  useEffect(() => {
    const handleResize = () => {
        setWindowWidth(window.innerWidth);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Animation styles for smooth transitions
  const overlayStyle = {
    ...modalStyles.overlay,
    animation: isClosing ? 'fadeOut 0.3s ease-out' : 'fadeIn 0.3s ease-out'
  };

  const contentStyle = {
    ...modalStyles.content,
    maxWidth: '700px',
    width: '90%',
    maxHeight: '90vh',
    overflowY: 'auto',
    animation: isClosing ? 'slideDown 0.3s ease-out' : 'slideUp 0.3s ease-out',
    transition: 'opacity 0.3s ease-out', // Smooth opacity changes between slides
    ...(isMobile() && {
      ...modalStyles.contentMobile,
      width: '95%',
      maxWidth: '100%',
      maxHeight: '95vh',
      padding: mobileSpacing.lg,
    })
  };

  // Keyframe animations
  const animations = `
    @keyframes fadeIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }
    @keyframes fadeOut {
      from { opacity: 1; }
      to { opacity: 0; }
    }
    @keyframes slideUp {
      from { 
        opacity: 0;
        transform: translateY(50px);
      }
      to { 
        opacity: 1;
        transform: translateY(0);
      }
    }
    @keyframes slideDown {
      from { 
        opacity: 1;
        transform: translateY(0);
      }
      to { 
        opacity: 0;
        transform: translateY(50px);
      }
    }
    @keyframes slideContentIn {
    from {
      opacity: 0;
      transform: translateX(20px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }
  `;

  const slides = [
    {
      title: "If you can't ask a judge, ask Judgy!",
      content: "Judgy helps you answer MTG rules questions, clarify card interactions, and understand rulings — anytime, anywhere.",
      image: "welcome-screen.png"
    },
    {
      title: "What Judgy Can (and Can't) Do",
      content: "Judgy is powerful but not a replacement for real judges. Always ask a judge first — but when you can't, Judgy has your back.",
      image: "judge-disclaimer.png"
    },
    {
      title: "Built on Official Rules and Examples",
      content: "Judgy draws from the full Comprehensive Rules, card data, and real ruling examples to help with even the weirdest interactions.",
      image: "answer-example.png"
    },
    {
      title: "Ask Anything — Improve Everything",
      content: "Type your question and get an instant answer. If something seems off, click \"Flag this answer\" to help make Judgy smarter.",
      image: "flag-feature.png"
    },
    {
      title: "Card Lookups Made Easy",
      content: "Use double square brackets for card names, like [[Black Lotus]], to instantly pull info from Scryfall.",
      image: "card-autocomplete.png"
    },
    {
      title: "Free to Use — Premium Available",
      content: "Judgy is free to use, forever. Upgrade to Premium for power-user features like conversation history, faster responses, and advanced tools—plus support ongoing development.",
      image: "premium-logo.svg",
      points: [
        "GPT-4.1 mini model for more detailed and accurate answers",
        "Conversation history - COMING SOON!",
        "Search and favorites - COMING SOON!",
        "Faster response times - COMING SOON!",
        "$5 monthly payment"
      ]
    }
  ];

  // Handles closing the modal with animation
  const handleClose = () => {
    trackUserEvent('how_it_works_closed', {
      completed_slides: currentSlide + 1,
      total_slides: slides.length,
    });
    setIsClosing(true); // Start the closing animation
    setTimeout(() => {
      onClose(); // Call onClose after animation duration
    }, 300); // Match duration of fadeOut animation
  };

  const nextSlide = () => {
    if (currentSlide < slides.length - 1) {
      setCurrentSlide(currentSlide + 1);
      trackUserEvent('how_it_works_next_slide', {
        slide_number: currentSlide + 2,
      });
    } else {
      handleClose(); // Close modal after last slide
    }
  };

  const prevSlide = () => {
    if (currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  const slide = slides[currentSlide];

  return (
    <>
      {/* Inject keyframe animations */}
      <style>{animations}</style>

      <div style={overlayStyle} onClick={handleClose}>
        <div
          onClick={(e) => e.stopPropagation()}
          style={contentStyle}
        >
          {/* Close button */}
          <button
            onClick={handleClose}
            style={modalStyles.closeButton}
            aria-label="Close"
          >
            ×
          </button>

          {/* Slide content */}
          <div
            key={currentSlide} // Key to trigger re-mount for animation,
            style={{
              textAlign: 'center',
              marginTop: spacing.xl,
              animation: 'slideContentIn 0.4s ease-out',
            }}
          >

            {/* Title */}
            <h2 style={{
              color: theme.text.primary,
              fontSize: typography.fontSize['2xl'],
              fontWeight: typography.fontWeight.bold,
              marginBottom: spacing.lg,
              fontFamily: typography.fontFamily.primary,
              lineHeight: typography.lineHeight.tight,
              ...(isMobile() && {
                fontSize: typography.mobileFontSize.xl,
                marginBottom: mobileSpacing.md,
              })
            }}>
              {slide.title}
            </h2>

            {/* Content */}
            <p style={{
              color: theme.text.secondary,
              fontSize: typography.fontSize.base,
              lineHeight: typography.lineHeight.relaxed,
              marginBottom: spacing.xl,
              fontFamily: typography.fontFamily.primary,
              ...(isMobile() && {
                fontSize: typography.mobileFontSize.base,
                marginBottom: mobileSpacing.lg,
                lineHeight: typography.lineHeight.normal,
              })
            }}>
              {slide.content}
            </p>

            {/* Screenshot/Image */}
            <div style={{
              marginBottom: spacing.xl,
              borderRadius: borderRadius.lg,
              overflow: 'hidden',
              boxShadow: theme.shadow.medium,
              backgroundColor: theme.bg.secondary,
              ...(isMobile() && {
                marginBottom: mobileSpacing.md,
                borderRadius: borderRadius.md,
              })
            }}>
              <img
                src={`/images/how-it-works/${slide.image}`}
                alt={slide.title}
                style={{
                  width: '100%',
                  height: 'auto',
                  display: 'block'
                }}
                onError={(e) => {
                  // Fallback if image doesn't load
                  e.target.style.display = 'none';
                }}
              />
            </div>

            {/* Bullet points */}
            {slide.points && (
              <ul style={{
                listStyle: 'none',
                padding: 0,
                margin: `0 0 ${spacing['2xl']} 0`,
                textAlign: 'left',
                ...(isMobile() && {
                  margin: `0 0 ${mobileSpacing.lg} 0`,
                })
              }}>
                {slide.points.map((point, index) => (
                  <li key={index} style={{
                    color: theme.text.primary,
                    fontSize: typography.fontSize.base,
                    marginBottom: spacing.md,
                    paddingLeft: spacing.xl,
                    position: 'relative',
                    fontFamily: typography.fontFamily.primary,
                    lineHeight: typography.lineHeight.normal,
                    ...(isMobile() && {
                      fontSize: typography.mobileFontSize.sm,
                      marginBottom: mobileSpacing.sm,
                      paddingLeft: mobileSpacing.lg,
                    })
                  }}>
                    <span style={{
                      position: 'absolute',
                      left: 0,
                      color: theme.brand.primary,
                      fontWeight: typography.fontWeight.bold
                    }}>
                      •
                    </span>
                    {point}
                  </li>
                ))}
              </ul>
            )}

            {/* Slide indicators (dots) */}
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              gap: spacing.sm,
              marginBottom: spacing.xl,
              ...(isMobile() && {
                gap: mobileSpacing.xs,
                marginBottom: mobileSpacing.md,
              })
            }}>
              {slides.map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToSlide(index)}
                  style={{
                    width: '10px',
                    height: '10px',
                    borderRadius: borderRadius.full,
                    backgroundColor: index === currentSlide 
                      ? theme.brand.primary 
                      : theme.border.light,
                    border: 'none',
                    cursor: 'pointer',
                    padding: 0,
                    transition: 'background-color 0.2s ease',
                    ...(isMobile() && {
                      width: '12px',
                      height: '12px',
                      minWidth: '12px',
                      minHeight: '12px',
                    })
                  }}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>

            {/* Navigation buttons */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: spacing.md,
              ...(isMobile() && {
                gap: mobileSpacing.sm,
                flexDirection: windowWidth < 350 ? 'column' : 'row',
              })
            }}>
              <button
                onClick={prevSlide}
                disabled={currentSlide === 0}
                style={mergeStyles(
                  buttonStyles.base,
                  buttonStyles.secondary,
                  currentSlide === 0 ? buttonStyles.disabled : {},
                  { flex: 1 },
                  isMobile() && {
                    ...buttonStyles.baseMobile,
                    width: windowWidth < 350 ? '100%' : 'auto',
                  }
                )}
              >
                {/* Add shorter text on mobile */}
                {isMobile() && windowWidth < 400 ? "←" : "← Previous"}
              </button>

              <button
                onClick={nextSlide}
                style={mergeStyles(
                  buttonStyles.base,
                  buttonStyles.primary,
                  { flex: 1 },
                  isMobile() && {
                    ...buttonStyles.baseMobile,
                    width: windowWidth < 350 ? '100%' : 'auto',
                  }
                )}
              >
                {/* Shorter text on mobile */}
                {currentSlide === slides.length - 1
                  ? (isMobile() && windowWidth < 400 ? 'Start!' : 'Get Started!')
                  : (isMobile() && windowWidth < 400 ? '→' : 'Next →')
                }
              </button>
            </div>

            {/* Slide counter */}
            <div style={{
              marginTop: spacing.lg,
              color: theme.text.muted,
              fontSize: typography.fontSize.sm,
              fontFamily: typography.fontFamily.primary,
              ...(isMobile() && {
                marginTop: mobileSpacing.md,
                fontSize: typography.mobileFontSize.xs,
              })
            }}>
              {currentSlide + 1} of {slides.length}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
